// Auto-logout when the stored JWT expires. The token lives on the `user`
// object in localStorage (see Login.vue); its `exp` claim drives a timer, and
// the axios interceptors in main.js cover tokens the server rejects early.

const EXPIRED_FLAG = 'sessionExpired';

let logoutTimer = null;
let loggingOut = false;

function getStoredToken() {
    try {
        const user = JSON.parse(window.localStorage.getItem('user'));
        return (user && user.token) || null;
    } catch (e) {
        return null;
    }
}

// Returns the token's expiry in ms, or null when it isn't a JWT with `exp`.
function getTokenExpiry(token) {
    try {
        const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const { exp } = JSON.parse(window.atob(payload));
        return typeof exp === 'number' ? exp * 1000 : null;
    } catch (e) {
        return null;
    }
}

class SessionService {
    isExpired() {
        const token = getStoredToken();
        if (!token) return false;
        const expiry = getTokenExpiry(token);
        return expiry !== null && Date.now() >= expiry;
    }

    // Same cleanup as the manual logout in Nav/Header/Dashboard (keep the
    // language), then a hard redirect so no stale logged-in state survives.
    // The toast is shown after the reload via showExpiredNotice().
    logout() {
        if (loggingOut) return;
        loggingOut = true;
        clearTimeout(logoutTimer);

        const langValue = window.localStorage.getItem('lang');
        window.localStorage.clear();
        if (langValue) window.localStorage.setItem('lang', langValue);
        window.sessionStorage.setItem(EXPIRED_FLAG, '1');
        window.location.href = '/login';
    }

    // Logs out immediately if already expired, otherwise schedules it.
    // Timers are paused while a laptop sleeps, so also re-check whenever the
    // tab becomes visible again.
    start() {
        this.checkAndSchedule();
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') this.checkAndSchedule();
        });
    }

    checkAndSchedule() {
        clearTimeout(logoutTimer);
        const token = getStoredToken();
        if (!token) return;

        const expiry = getTokenExpiry(token);
        if (expiry === null) return;

        const remaining = expiry - Date.now();
        if (remaining <= 0) {
            this.logout();
        } else {
            // setTimeout overflows above ~24.8 days; re-check later instead.
            logoutTimer = setTimeout(() => this.checkAndSchedule(), Math.min(remaining, 2147483647));
        }
    }

    showExpiredNotice(toast, message) {
        if (window.sessionStorage.getItem(EXPIRED_FLAG)) {
            window.sessionStorage.removeItem(EXPIRED_FLAG);
            toast.warning(message);
        }
    }
}

export default new SessionService();
